import React, { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import HelpIcon from '@material-ui/icons/Help';
import { KeywordPicker } from 'common';
import differenceBy from 'lodash/differenceBy';
import uniqBy from 'lodash/uniqBy';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { Permissions, userHasPermission, userHasRoles } from 'services/Authorization';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPRadioGroup from 'UI/components/molecules/FPRadioGroup';
import LocationForm from 'UI/components/organisms/LocationForm';
import PhoneNumbersForm from 'UI/components/organisms/PhoneNumbersForm';
import { BinaryOptions } from 'UI/constants/defaults';
import { FORM_SPACING, getGridColumnSize } from 'UI/constants/dimensions';
import { Endpoints } from 'UI/constants/endpoints';
import { CompanyType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import {
  EXT_PHONE_VALIDATION,
  idOptionSelected,
  industrySpecialtyOptionLabel,
  nestTernary,
  PHONE_VALIDATION,
  REQUIRED_VALIDATION,
  titleOptionLabel,
  URL_VALIDATION,
  VALIDATION_REGEXS
} from 'UI/utils';
import { buildBasePropsForField } from 'UI/utils/forms';
import { DEFAULT_DISPLAY_TEXT_KEY } from 'UI/utils/inventory';
import { LabelRenderers, Selectors } from 'UI/utils/renderers';

import { DEFAULT_PHONE_ROW } from '../PhoneNumbersForm/utils';

import { FormFieldsMap, SubspecialtiesCatalogKey } from './fields';
import { useStyles } from './styles';

const chainedSelects = {
  [FormFieldsMap.State.key]: [FormFieldsMap.City.key, FormFieldsMap.Zip.key],
  [FormFieldsMap.City.key]: [FormFieldsMap.Zip.key]
};

const idExtractor = ({ id }) => id;
const phoneProps = { 'aria-label': 'phone' };

const getPhoneInputType = phone =>
  phone && phone.length > PHONE_VALIDATION.maxLength.value ? 'text' : 'phone';

/**
 * @param {Object} props
 * @param {boolean} [props.disabled]
 * @param {array} [props.disabledEditKeys]
 */
const CompanyForm = ({ disabled, disabledEditKeys }) => {
  const classes = useStyles();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const canEditCompanyPriorityTarget =
    checkIfFeatureFlagEnabled(FeatureFlags.CompaniesTargetPriority) &&
    (userHasPermission(Permissions.Company.editPriorityTarget) ||
      userHasRoles([
        Roles.Operations,
        Roles.DataCoordinator,
        Roles.Leadership,
        Roles.ProductionDirector
      ]));

  const { register, errors, setValue, watch, getValues } = useFormContext();

  useEffect(() => {
    register({ name: FormFieldsMap.Id.key });

    register(
      { name: FormFieldsMap.Specialties.key },
      {
        validate(values) {
          return values?.length > 0 || REQUIRED_VALIDATION.required;
        }
      }
    );
    register({ name: FormFieldsMap.Phone.key }, { ...REQUIRED_VALIDATION, ...PHONE_VALIDATION });
    register({ name: FormFieldsMap.Type.key });
    register({ name: SubspecialtiesCatalogKey });
    register({
      name: FormFieldsMap.Phones.key,
      value: [DEFAULT_PHONE_ROW]
    });

    register({ name: FormFieldsMap.CompanyTarget.key });
    register({ name: FormFieldsMap.KeywordTags.key });
  }, [register, canEditCompanyPriorityTarget]);

  useEffect(() => {
    register(
      { name: FormFieldsMap.Subspecialties.key },
      {
        validate(value) {
          const subspecialties = getValues(SubspecialtiesCatalogKey) || [];
          const selectedSubspecialties = value || [];
          const specialtiesWithRequiredSubspecialties = uniqBy(subspecialties, 'specialty_id');
          const missingSubspecialties = differenceBy(
            specialtiesWithRequiredSubspecialties,
            selectedSubspecialties,
            'specialty_id'
          );

          return (
            missingSubspecialties.length === 0 ||
            `A specialty for ${missingSubspecialties[0].specialty} is required`
          );
        }
      }
    );
  }, [register, getValues]);

  const handlePhoneChange = (name, value) => setValue(name, value, true);

  const handleComboChange = (name, value) => {
    setValue(name, value, true);

    if (name && chainedSelects[name]) {
      chainedSelects[name].forEach(chainedSelect => setValue(chainedSelect, null));
    }
  };

  const handleSpecialtyChange = (name, value) => {
    const specialtyIds = value ? value.map(idExtractor) : [];
    const currentSubspecialties = formValues[FormFieldsMap.Subspecialties.key];
    const newSubspecialties = currentSubspecialties.filter(subspec =>
      specialtyIds.includes(subspec.specialty_id)
    );

    setValue(name, value, true);

    const shouldClearSomeSubspecialties = currentSubspecialties.length !== newSubspecialties.length;

    shouldClearSomeSubspecialties && setValue(FormFieldsMap.Subspecialties.key, newSubspecialties);
  };

  const handleSubspecialtiesLoaded = useCallback(
    options => {
      setValue(SubspecialtiesCatalogKey, options);
    },
    [setValue]
  );

  const formValues = watch();
  const companyId = formValues[FormFieldsMap.Id.key];
  const typeId = formValues[FormFieldsMap.Type.key]?.id;

  const selectedSpecialties = formValues[FormFieldsMap.Specialties.key];
  const specialtyIds = selectedSpecialties.map(idExtractor);
  const availableSubspecialties = formValues[SubspecialtiesCatalogKey];
  const areSubspecialtiesRequired =
    selectedSpecialties?.length > 0 && availableSubspecialties?.length > 0;

  const canChangeType = userHasRoles([Roles.Operations, Roles.DataCoordinator]);
  const isEditing = !!companyId;

  const shouldDisplayLegacyPhoneFields = !checkIfFeatureFlagEnabled(
    FeatureFlags.CompaniesMultiplePhones
  );

  const isDisabled = key => {
    return disabled || (isEditing && disabledEditKeys.includes(key));
  };

  const priorityTargetRadioValue = nestTernary(
    formValues[FormFieldsMap.CompanyTarget.key] === null,
    null,
    formValues[FormFieldsMap.CompanyTarget.key] ? '1' : '0'
  );

  const handleChangeCheckbox = (event, value) => {
    const booleanValue = value === '1';

    setValue(FormFieldsMap.CompanyTarget.key, booleanValue, true);
  };

  const handleChangeKeywords = (name, value) => {
    setValue(name, value, true);
  };

  const { xs, sm, md, lg } = getGridColumnSize(isEditing);

  return (
    <Grid container className="creation-form-layout">
      <Grid container item spacing={FORM_SPACING}>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.Name.key, errors)}
            label={strings.companies.creation.fields[FormFieldsMap.Name.key]}
            inputRef={register(REQUIRED_VALIDATION)}
            disabled={isDisabled(FormFieldsMap.Name.key)}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.LegalName.key, errors)}
            label={strings.companies.creation.fields[FormFieldsMap.LegalName.key]}
            inputRef={register()}
            disabled={isDisabled(FormFieldsMap.LegalName.key)}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <AutocompleteSelect
            {...buildBasePropsForField(FormFieldsMap.Specialties.key, errors)}
            getOptionLabel={industrySpecialtyOptionLabel}
            getOptionSelected={idOptionSelected}
            groupBy={option => option.industry_title}
            multiple
            onSelect={handleSpecialtyChange}
            placeholder={strings.companies.creation.fields[FormFieldsMap.Specialty.key]}
            renderOption={titleOptionLabel}
            selectedValue={selectedSpecialties}
            url={Endpoints.Specialties}
            disabled={isDisabled(FormFieldsMap.Specialties.key)}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <AutocompleteSelect
            {...buildBasePropsForField(FormFieldsMap.Subspecialties.key, errors)}
            width="100%"
            selectedValue={formValues[FormFieldsMap.Subspecialties.key]}
            placeholder={`${strings.companies.creation.fields[FormFieldsMap.Subspecialty.key]}${
              areSubspecialtiesRequired ? ' *' : ''
            }`}
            url={
              specialtyIds.length > 0
                ? `${Endpoints.SubspecialtiesSearch}/by-specialty?specialtyIds=${specialtyIds.join(
                    ','
                  )}`
                : ''
            }
            multiple
            getOptionSelected={Selectors.byId}
            getOptionLabel={LabelRenderers.subspecialty}
            renderOption={LabelRenderers.title}
            groupBy={option => `${option.industry} › ${option.specialty}`}
            onSelect={handleComboChange}
            onOptionsLoaded={handleSubspecialtiesLoaded}
            disabled={isDisabled(FormFieldsMap.Subspecialties.key)}
          />
        </Grid>
        <LocationForm
          state={formValues[FormFieldsMap.State.key]}
          city={formValues[FormFieldsMap.City.key]}
          zip={formValues[FormFieldsMap.Zip.key]}
          handleComboChange={handleComboChange}
          isEditing={isEditing}
          disabled={isDisabled(FormFieldsMap.State.key)}
        />
        {shouldDisplayLegacyPhoneFields && (
          <>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <TextBox
                {...buildBasePropsForField(FormFieldsMap.Phone.key, errors)}
                label={strings.companies.creation.fields[FormFieldsMap.Phone.key].label}
                placeholder={strings.companies.creation.fields[FormFieldsMap.Phone.key].placeholder}
                onChange={handlePhoneChange}
                value={formValues[FormFieldsMap.Phone.key] || ''}
                inputType={getPhoneInputType(formValues[FormFieldsMap.Phone.key])}
                inputProps={phoneProps}
                disabled={isDisabled(FormFieldsMap.Phone.key)}
              />
            </Grid>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <TextBox
                {...buildBasePropsForField(FormFieldsMap.Ext.key, errors)}
                label={strings.companies.creation.fields[FormFieldsMap.Ext.key].label}
                placeholder={strings.companies.creation.fields[FormFieldsMap.Ext.key].placeholder}
                inputRef={register(EXT_PHONE_VALIDATION)}
                disabled={isDisabled(FormFieldsMap.Ext.key)}
              />
            </Grid>
          </>
        )}
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.Email.key, errors)}
            label={strings.companies.creation.fields[FormFieldsMap.Email.key]}
            inputRef={register({
              pattern: {
                value: VALIDATION_REGEXS.EMAIL,
                message: strings.shared.validations.invalid
              }
            })}
            disabled={isDisabled(FormFieldsMap.Email.key)}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.Address.key, errors)}
            label={strings.companies.creation.fields[FormFieldsMap.Address.key]}
            inputRef={register(REQUIRED_VALIDATION)}
            disabled={isDisabled(FormFieldsMap.Address.key)}
          />
        </Grid>

        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.Website.key, errors)}
            label={strings.companies.creation.fields[FormFieldsMap.Website.key]}
            inputRef={register(URL_VALIDATION)}
            disabled={isDisabled(FormFieldsMap.Website.key)}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.LinkedinUrl.key, errors)}
            label={strings.companies.creation.fields[FormFieldsMap.LinkedinUrl.key]}
            inputRef={register(URL_VALIDATION)}
            disabled={isDisabled(FormFieldsMap.LinkedinUrl.key)}
          />
        </Grid>
        <Grid item xs={xs} sm={sm} md={md} lg={lg}>
          <TextBox
            {...buildBasePropsForField(FormFieldsMap.AccountsPayableEmail.key, errors)}
            label={strings.companies.creation.fields[FormFieldsMap.AccountsPayableEmail.key]}
            inputRef={register({
              pattern: {
                value: VALIDATION_REGEXS.EMAIL,
                message: strings.shared.validations.invalid
              }
            })}
            disabled={isDisabled(FormFieldsMap.AccountsPayableEmail.key)}
          />
        </Grid>
        {isEditing && (
          <>
            <Grid item xs={xs} sm={sm} md={md} lg={lg}>
              <AutocompleteSelect
                {...buildBasePropsForField(FormFieldsMap.Type.key, errors)}
                width="100%"
                placeholder={strings.companies.creation.fields[FormFieldsMap.Type.key]}
                selectedValue={formValues[FormFieldsMap.Type.key]}
                url={`${Endpoints.Companies}/${Endpoints.Types}`}
                onSelect={handleComboChange}
                disabled={!canChangeType}
              />
            </Grid>

            {typeId === CompanyType.NotSigned && !canChangeType && (
              <Grid item xs={xs} sm={sm} md={md} lg={lg}>
                <FormHelperText>
                  <HelpIcon color="disabled" />
                  {strings.companies.typeHelper}
                </FormHelperText>
              </Grid>
            )}

            <When condition={canEditCompanyPriorityTarget}>
              <Grid item xs={12}>
                <FPRadioGroup
                  label={strings.companies.creation.targetCompany.label}
                  name={FormFieldsMap.CompanyTarget.key}
                  onChange={handleChangeCheckbox}
                  options={BinaryOptions}
                  value={priorityTargetRadioValue}
                />
                <FormHelperText>
                  {strings.companies.creation.targetCompany.helperText}
                </FormHelperText>
              </Grid>
            </When>
          </>
        )}
      </Grid>

      <Grid container spacing={2} className={classes.keywordsContainer}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">
            {strings.inventoryProfiles.common.keywords.title}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <KeywordPicker
            allowNewItems
            displayTextKey={DEFAULT_DISPLAY_TEXT_KEY}
            endpoint={Endpoints.KeywordTags}
            name={FormFieldsMap.KeywordTags.key}
            onSelect={handleChangeKeywords}
            placeholder={strings.inventoryProfiles.common.keywords.placeholder}
            selectedValue={formValues?.[FormFieldsMap.KeywordTags.key] ?? []}
            typeaheadLimit={25}
          />
        </Grid>
      </Grid>

      {checkIfFeatureFlagEnabled(FeatureFlags.CompaniesMultiplePhones) && (
        <PhoneNumbersForm
          isEditing={isEditing}
          disabled={isDisabled(FormFieldsMap.Phones.key)}
          isRequired
        />
      )}
    </Grid>
  );
};

CompanyForm.defaultProps = {
  disabled: false,
  disabledEditKeys: []
};

export default CompanyForm;
